import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import * as Yup from 'yup'
import styles from './OneStepSignUp.module.scss'

import {
  Button,
  Flex,
  Loader,
  MultiStepForm,
  MultiStepFormHeader,
  MultiStepFormFieldType as FieldType,
  MultiStepFormStep as FormStep,
  Spacer,
  Tooltip
} from '@percent/lemonade'
import {
  useMutation,
  useCausesPortalAnalytics,
  useQuery,
  useDefaultPasswordCriteria,
  useGrantsAttributionRedirect,
  useUtmParams,
  useSignUpAttribution,
  useLocale,
  usePostHogFeatureFlag,
  usePostHogFeatureFlagVariant,
  useFeatureFlagsReady,
  useFeatureFlagsReadyWithTimeout,
  useCaptcha,
  POSTHOG_FEATURE_FLAGS
} from '@percent/cause-dashboard/common/hooks'
import { SignUpEventName } from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/causeDashboardAnalytics.types'
import { passwordRequirementRegExp, phoneRegExp } from '@percent/cause-dashboard/common/utility/validation'
import { config } from '@percent/cause-dashboard/config'
import { CaptchaWidget } from '@percent/cause-dashboard/common/components/captchaWidget/CaptchaWidget'
import { CaptchaSlowWarning } from '@percent/cause-dashboard/common/components/captchaWidget/CaptchaSlowWarning'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { emailRegex } from '@percent/utility'
import { FormikValues } from 'formik'
import { useTranslation } from 'react-i18next'
import { RoutePath } from '../../routes/Routes'
import { PartnerSplitPagesContainer as SplitPagesContainer } from '../../layout/partnerSplitPagesContainer/PartnerSplitPagesContainer'
import { OrganisationSignupCard } from '@percent/cause-dashboard/common/components/organisationSignupCard/OrganisationSignupCard'
import { SIGN_UP_EMAIL_STORAGE_KEY } from '../signUpCheckEmail/signUpEmailStorage'
import { Alpha2Code, alpha3ToAlpha2 } from 'i18n-iso-countries'

interface SignUpData {
  firstName: string
  lastName: string
  email: string
  password: string
  marketingConsent: boolean
  onboardingQuestionResponses: { questionId: string; answer: string }[]
  phone?: string
  token?: string | null
}

type SignupPhoneVariant = 'control' | 'phone_required' | 'excluded'

// Separates a reload from a remount in analytics. Not crypto.randomUUID, which needs a secure context, and
// feature stacks serve plain http.
const pageLoadId = Array.from(crypto.getRandomValues(new Uint32Array(2)), value => value.toString(36)).join('-')

export const OneStepSignUp = () => {
  const { t } = useTranslation()
  const { authService, causeService } = useServices()
  const { track, identify } = useCausesPortalAnalytics()
  const { signUpAttribution } = useSignUpAttribution()
  const { utmParams } = useUtmParams()
  const { locale } = useLocale()
  const navigate = useNavigate()
  const defaultPasswordCriteria = useDefaultPasswordCriteria()
  const hasTrackedPageVisit = useRef(false)
  const hasTrackedFormStarted = useRef(false)
  const hasTrackedPhoneEntered = useRef(false)
  const isCaptchaEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_SIGN_UP_CAPTCHA)
  const statusCheckVariant = usePostHogFeatureFlagVariant(POSTHOG_FEATURE_FLAGS.CP_AB_TESTING_STATUS_CHECK_PAGE)
  const phoneRequiredFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_SIGNUP_PHONE_REQUIRED_US)
  const areFlagsLoaded = useFeatureFlagsReady()
  const areFlagsReady = useFeatureFlagsReadyWithTimeout()
  const [formError, setFormError] = useState<string | undefined>(undefined)
  // Separate from formError so a successful retry clears it; formError only resets when the mutation result changes.
  const [captchaError, setCaptchaError] = useState<string>()

  useGrantsAttributionRedirect()

  const handleRequestToJoinClick = () => {
    navigate(`${RoutePath.REQUEST_TO_JOIN}?id=${orgData?.id}&name=${orgData?.name}`)
  }

  const { search } = useLocation()
  const urlParams = new URLSearchParams(search)

  const emailParam = urlParams.get('email') || ''
  const orgIdParam = urlParams.get('organization-id') || ''

  const [{ data: orgData, isLoading: isOrgLoading, error: isOrgError }] = useQuery(causeService.getOrganisation, {
    organisationId: orgIdParam || '',
    withPartnerKey: true
  })

  const [signupPhoneVariant, setSignupPhoneVariant] = useState<SignupPhoneVariant>()

  if (!signupPhoneVariant && areFlagsReady && orgData) {
    if (orgData.countryCode !== 'USA' || !areFlagsLoaded) {
      setSignupPhoneVariant('excluded')
    } else {
      setSignupPhoneVariant(phoneRequiredFlag ? 'phone_required' : 'control')
    }
  }

  const isInPhoneTest = signupPhoneVariant === 'control' || signupPhoneVariant === 'phone_required'
  const isPhoneRequired = signupPhoneVariant === 'phone_required'
  const { recaptchaRef, executeCaptcha, isVerifying, isSlow } = useCaptcha('sign_up', {
    pageLoadId,
    countryCode: orgData?.countryCode,
    variant: signupPhoneVariant
  })

  const trackSignup = useCallback(
    (name: SignUpEventName, props: Record<string, unknown> = {}) =>
      track(name, { variant: signupPhoneVariant, countryCode: orgData?.countryCode, pageLoadId, ...props }),
    [track, signupPhoneVariant, orgData?.countryCode]
  )

  useEffect(() => {
    if (areFlagsReady && orgData?.name && !hasTrackedPageVisit.current) {
      hasTrackedPageVisit.current = true
      const trackPageVisit = async () => {
        await trackSignup(SignUpEventName.SIGNUP_PAGE_VISITED, {
          organisationName: orgData?.name,
          reason: signUpAttribution?.reason,
          partnerId: signUpAttribution?.partnerId,
          experimentVariant: statusCheckVariant
        })
      }
      trackPageVisit()
    }
  }, [
    areFlagsReady,
    trackSignup,
    orgData?.name,
    signUpAttribution?.reason,
    signUpAttribution?.partnerId,
    signUpAttribution?.emailVariant,
    statusCheckVariant
  ])

  const formSteps = useMemo(() => {
    const createAccountValidationSchema = Yup.object().shape({
      firstName: Yup.string()
        .required(t('form.isRequired', { name: 'First name' }))
        .min(1, t('form.minLength', { name: 'First name', length: 1 }))
        .max(255, t('form.maxLength', { name: 'First name', length: 255 })),
      lastName: Yup.string()
        .required(t('form.isRequired', { name: 'Last name' }))
        .min(1, t('form.minLength', { name: 'Last name', length: 1 }))
        .max(255, t('form.maxLength', { name: 'Last name', length: 255 })),
      email: Yup.string()
        .required(t('form.isRequired', { name: 'Email' }))
        .matches(emailRegex, t('errorMessage.validEmail')),
      phone: isPhoneRequired
        ? Yup.string()
            .required(t('form.isRequired', { name: 'Phone number' }))
            .matches(phoneRegExp, t('errorMessage.notValidPhoneNumber'))
        : Yup.string().optional(),
      password: Yup.string()
        .min(8, t('form.minLength', { name: 'Password', length: 8 }))
        .max(64, t('form.maxLength', { name: 'Password', length: 64 }))
        .matches(passwordRequirementRegExp, t('errorMessage.passwordRequirement'))
        .required(t('form.isRequired', { name: 'Password' })),
      agreement: Yup.boolean().oneOf([true], t('errorMessage.acceptTerms')).required(t('errorMessage.acceptTerms')),
      marketingConsent: Yup.boolean().optional()
    })

    const getCountryCode = (country?: string): Alpha2Code => {
      if (!country) return 'US'
      return alpha3ToAlpha2(country) as Alpha2Code
    }

    const stepFields = [
      {
        name: 'email',
        label: t('form.email'),
        placeholder: t('form.enterEmailAddress'),
        type: 'text' as FieldType,
        defaultValue: emailParam,
        description: t('form.emailWorkAddressHint')
      },
      {
        name: 'firstName',
        label: t('form.firstName'),
        placeholder: t('form.firstNameDescription'),
        type: 'text' as FieldType
      },
      {
        name: 'lastName',
        label: t('form.lastName'),
        placeholder: t('form.lastNameDescription'),
        type: 'text' as FieldType
      },
      {
        name: 'phone',
        label: t('form.signUp.phoneNumber.title'),
        placeholder: t('form.signUp.phoneNumber.placeholder'),
        type: 'phone' as FieldType,
        optional: !isPhoneRequired,
        defaultCountry: getCountryCode(orgData?.countryCode)
      },
      {
        name: 'password',
        label: t('form.password'),
        placeholder: t('form.createAPassword'),
        type: 'password' as FieldType
      },
      {
        name: 'agreement',
        checkboxLabel: (
          <span className={styles.checkboxLabel}>
            {t('typography.iAcceptThe')}{' '}
            <a target="_blank" rel="noreferrer" href={config.urls.terms}>
              {t('typography.termsOfService')}
            </a>{' '}
            {t('typography.and')}{' '}
            <a target="_blank" rel="noreferrer" href={config.urls.privacyAndPolicy}>
              {t('typography.privacyPolicy')}
            </a>
          </span>
        ),
        type: 'checkbox' as FieldType
      },
      {
        name: 'marketingConsent',
        checkboxLabel: (
          <span className={`${styles.checkboxLabel} ${styles.marketingConsent}`}>
            {t('typography.marketingConsentStart')}{' '}
            <button type="button" className={styles.tooltipTrigger}>
              <Tooltip content={t('typography.marketingConsentDetail')}>
                {t('typography.marketingConsentTooltipTrigger')}
              </Tooltip>
            </button>{' '}
            {t('typography.marketingConsentEnd')}
          </span>
        ),
        type: 'checkbox' as FieldType
      }
    ]

    const formSteps: FormStep[] = [
      {
        stepTitle: t('form.createYourAccount'),
        stepSubtitle: <OrganisationSignupCard orgData={orgData} />,
        fields: stepFields,
        validationSchema: createAccountValidationSchema,
        buttonText: t('button.createAccount')
      }
    ]

    return formSteps
  }, [t, orgData, emailParam, isPhoneRequired])

  useEffect(() => {
    const agreementCheckbox = document.querySelector('input[name="agreement"]') as HTMLInputElement
    const marketingCheckbox = document.querySelector('input[name="marketingConsent"]') as HTMLInputElement

    if (agreementCheckbox) {
      agreementCheckbox.dataset.hsFieldName = 'non_hubspot_form_agree_to_terms_of_service_and_privacy_policy'
    }

    if (marketingCheckbox) {
      marketingCheckbox.dataset.hsFieldName = 'non_hubspot_form_marketing_consent'
    }
  }, [formSteps])

  const [{ errorMessage, isLoading, error }, { apiFunc: signUpUser }] = useMutation(
    async ({ firstName, lastName, password, email, marketingConsent, phone: phoneNumber, token }: SignUpData) => {
      await authService.signUp({
        email,
        password,
        fullName: `${firstName} ${lastName}`,
        phoneNumber,
        preferredLanguage: locale,
        token,
        organisationId: orgIdParam || undefined,
        marketingConsent: !!marketingConsent,
        // reason and partnerId come from URLSearchParams.get (string | null), and the API rejects null
        signUpAttribution: {
          reason: signUpAttribution?.reason ?? undefined,
          partnerId: signUpAttribution?.partnerId ?? undefined,
          utmParams: utmParams ?? undefined
        }
      })

      const traits = {
        organisationId: orgData?.id,
        partnerId: signUpAttribution?.partnerId,
        email: email,
        organisation: orgData?.name,
        country: orgData?.countryCode
      }

      await identify({
        traits
      })

      await trackSignup(SignUpEventName.SIGNUP_COMPLETED, {
        organisationName: orgData?.name,
        reason: signUpAttribution?.reason,
        partnerId: signUpAttribution?.partnerId,
        experimentVariant: statusCheckVariant,
        ...utmParams
      })

      sessionStorage.setItem(SIGN_UP_EMAIL_STORAGE_KEY, email.trim())
      navigate(RoutePath.SIGNUP_CHECK_EMAIL, { replace: true, state: { email: email.trim() } })
    }
  )

  useEffect(() => {
    // Error is wrongly typed as string inside useMutation hook, as in MultiStepAddOrganization
    const anyError = error as any

    if (anyError?.code === 'iam/invalid_captcha_token') {
      setFormError(t('errorMessage.captchaFailed'))
    } else if (anyError?.code === 'claim/organisation_not_found') {
      setFormError(t('errorMessage.signUpOrganisationNotFound'))
    } else if (anyError?.code === 'claim/already_claimed') {
      setFormError(t('errorMessage.signUpOrganisationAlreadyClaimed'))
    } else {
      setFormError(errorMessage)
    }
  }, [errorMessage, error, t])

  const handleFieldInteraction = (fieldName: string) => {
    if (!hasTrackedFormStarted.current) {
      hasTrackedFormStarted.current = true
      trackSignup(SignUpEventName.SIGNUP_FORM_STARTED)
    }
    if (fieldName === 'phone' && !hasTrackedPhoneEntered.current) {
      hasTrackedPhoneEntered.current = true
      trackSignup(SignUpEventName.SIGNUP_PHONE_ENTERED)
    }
  }

  const onSubmit = async (values: FormikValues) => {
    let token: string | null | undefined
    setCaptchaError(undefined)

    if (isCaptchaEnabled) {
      try {
        token = await executeCaptcha()
      } catch {
        setCaptchaError(t('errorMessage.captchaFailed'))

        return
      }
    }

    trackSignup(SignUpEventName.SIGNUP_SUBMIT_ATTEMPTED)

    signUpUser({ ...(values as SignUpData), token })
  }

  if (isOrgError) {
    return <Navigate to="/" state={{ organizationNotFoundAlert: true }} replace />
  }

  const getFormSectionLayout = () => {
    const isOrgClaimed = !!orgData?.claimedAt
    // needs to show loader when org data or questions are fetching
    // if not, there is `Maximum update depth exceeded` error
    if (isOrgLoading || !areFlagsReady) {
      return <Loader />
    } else if (isOrgClaimed) {
      track(SignUpEventName.ORG_ALREADY_CLAIMED_ERROR, {
        organisationName: orgData?.name
      })
      return (
        <>
          <MultiStepFormHeader
            data-testId="error-header"
            title={t('form.yourDetails')}
            subtitle={t('form.requestToJoinDescription')}
          />
          <Spacer size={6} axis="vertical" />
          <Button variant="primary" stretch onClick={handleRequestToJoinClick}>
            {t('button.requestToJoin.title')}
          </Button>
        </>
      )
    } else {
      return (
        <>
          <MultiStepForm
            steps={formSteps}
            onSubmit={onSubmit}
            errorMessage={captchaError ?? formError}
            isLoading={isLoading || isVerifying}
            passwordCriteria={defaultPasswordCriteria}
            onFieldInteraction={handleFieldInteraction}
          />
          <CaptchaSlowWarning isSlow={isSlow} />
          <Spacer size={6} axis="vertical" />
        </>
      )
    }
  }

  return (
    <SplitPagesContainer>
      <Flex className={styles.formWrapper}>{getFormSectionLayout()}</Flex>
      {isCaptchaEnabled && <CaptchaWidget ref={recaptchaRef} />}
    </SplitPagesContainer>
  )
}
